import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataListDefault, DataListResponse, WorkTab } from "types/ui/card-item";
import TabsLayout from "components/tabs-layout";
import BoardBackground from "components/board/board-bg";
import CalendarComponent from "components/calendar";
import { useSharedState } from "state";
import { TaskListResponse, TaskProps } from "types/task";
import { CalendarEvent } from "types/ui/calendar-item";
import { TaskStatusEnum } from "types/dict";

export default function TasksPage() {
  const { tasks, getTasksData, calendarTasks, getCalendarTasksData, currentPageTab, currentRange } = useSharedState();
  const [searchParams] = useSearchParams();
  const taskType = searchParams.get("type");

  const [taskCards, setTaskCards] = useState<DataListResponse>(DataListDefault);

  useEffect(() => {
    updateTasksList();
  }, [currentPageTab, currentRange, taskType]);

  useEffect(() => {
    setTaskCards(convertTasksToCards(tasks));
  }, [tasks]);

  const updateTasksList = async () => {
    currentPageTab === "cards" && (await getTasksData());
    currentPageTab === "calendar" && (await getCalendarTasksData());
  };

  const getTaskColor = (status?: number): string => {
    switch (status) {
      case TaskStatusEnum.TO_DO:
        return "#f76707";

      case TaskStatusEnum.IN_PROGRESS:
        return "#206bc4";

      case TaskStatusEnum.FOR_APPROVAL:
        return "#ae3ec9";

      case TaskStatusEnum.REJECTED:
        return "#f76707";

      case TaskStatusEnum.COMPLETED:
        return "#2fb344";

      default:
        return "#206bc4";
    }
  };

  const formattedCards = (tasksByStatus: TaskProps[]) => {
    return tasksByStatus?.map((task) => ({
      id: task.task_request_id,
      title: task.task_name,
      description: task.description,
      status: task.status,
      dueDate: task.deadline,
      created_by: task.creator_name,
      assigned_to: task.assigned_to_name,
      priority: task.priority as "Высокая" | "Средняя" | "Низкая" | undefined,
    }));
  };

  const formattedEvents: CalendarEvent[] = useMemo(() => {
    return calendarTasks.map((task) => ({
      title: task.task_name,
      start: new Date(task.request_date),
      end: new Date(task.deadline),
      resource: {
        id: task.task_request_id,
        color: getTaskColor(task.status),
      },
    }));
  }, [calendarTasks]);

  const convertTasksToCards = (tasks: TaskListResponse): DataListResponse => {
    return {
      "0": {
        data: formattedCards(tasks["0"]?.data) || [],
        total_count: tasks["0"]?.total_count,
      },
      "1": {
        data: formattedCards(tasks["1"]?.data) || [],
        total_count: tasks["1"]?.total_count,
      },
      "2": {
        data: formattedCards(tasks["2"]?.data) || [],
        total_count: tasks["2"]?.total_count,
      },
      "3": {
        data: formattedCards(tasks["3"]?.data) || [],
        total_count: tasks["3"]?.total_count,
      },
      "4": {
        data: formattedCards(tasks["4"]?.data) || [],
        total_count: tasks["4"]?.total_count,
      },
    };
  };

  const getTotalTasksCount = (): number => {
    return Object.values(tasks).reduce((sum, current) => sum + (current?.total_count || 0), 0);
  };

  const task_tabs: WorkTab[] = [
    {
      id: "cards",
      title: "Задачи по статусам",
      component: <BoardBackground data={taskCards} />,
    },
    { id: "calendar", title: "Календарь", component: <CalendarComponent data={formattedEvents} /> },
  ];

  return (
    <TabsLayout
      tabs={task_tabs}
      header={taskType === "other" ? "Другие задачи" : "Мои задачи"}
      subheaderText={`Количество задач: ${getTotalTasksCount()}`}
      buttonText="Новая задача"
    />
  );
}
